import React, { useState, useEffect } from 'react';
import api from '../api';
import { 
    FaShoppingCart, FaSpinner, FaExclamationTriangle, FaCheckCircle, 
    FaClock, FaSync, FaDownload, FaEye, FaTimes, FaDollarSign, 
    FaBoxOpen, FaCalendarAlt, FaArrowUp, FaArrowDown, FaMinus,
    FaFilter, FaShare, FaCopy, FaWhatsapp, FaSortAmountDown,
    FaSortAmountUp, FaSearch, FaChartLine,
    FaInfoCircle, FaLightbulb, FaFileExport,
    FaFlag
} from 'react-icons/fa';

const ShoppingDashboard = () => {
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filtro, setFiltro] = useState('urgentes');
    const [ordenacao, setOrdenacao] = useState('urgencia');
    const [produtoDetalhado, setProdutoDetalhado] = useState(null);
    
    // ✅ NOVOS ESTADOS PARA RECURSOS APRIMORADOS
    const [searchTerm, setSearchTerm] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState('cards'); // 'cards', 'list'
    const [priceFilter, setPriceFilter] = useState('all'); // 'all', 'low', 'medium', 'high'
    const [dateRange, setDateRange] = useState('all'); // 'today', 'week', 'month', 'all'
    const [priorityItems, setPriorityItems] = useState(new Set());
    const [notes, setNotes] = useState({});
    const [showStats, setShowStats] = useState(true);
    const [compactMode, setCompactMode] = useState(false);
    const [sortOrder, setSortOrder] = useState('desc');

    useEffect(() => {
        carregarDados();
        // Carregar preferências do localStorage
        carregarPreferencias();
    }, []);

    const carregarPreferencias = () => {
        try {
            const prefs = JSON.parse(localStorage.getItem('shopping_preferences') || '{}');
            setViewMode(prefs.viewMode || 'cards');
            setShowStats(prefs.showStats !== false);
            setCompactMode(prefs.compactMode || false);
            setPriorityItems(new Set(prefs.priorityItems || []));
            setNotes(prefs.notes || {});
        } catch (error) {
            console.log('Erro ao carregar preferências:', error);
        }
    };

    const salvarPreferencias = () => {
        const prefs = {
            viewMode,
            showStats,
            compactMode,
            priorityItems: Array.from(priorityItems),
            notes
        };
        localStorage.setItem('shopping_preferences', JSON.stringify(prefs));
    };

    // Salvar preferências quando mudarem
    useEffect(() => {
        salvarPreferencias();
    }, [viewMode, showStats, compactMode, priorityItems, notes]);

    const carregarDados = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await api.get('/previsoes/resumo');
            setDados(response.data);
            
        } catch (error) {
            console.error('❌ Erro ao carregar dados:', error);
            if (error.response?.status === 404) {
                await carregarDadosLegado();
            } else {
                setError('Erro ao carregar dados. Verifique sua conexão.');
            }
        } finally {
            setLoading(false);
        }
    };

    const carregarDadosLegado = async () => {
        try {
            const produtosResponse = await api.get('/produtos?detalhes=true');
            const todosProdutos = produtosResponse.data;
            
            const produtosComPrevisao = await Promise.all(
                todosProdutos.slice(0, 20).map(async (produto) => {
                    try {
                        const previsaoResponse = await api.get(`/produtos/${produto.id}/previsao`);
                        return {
                            produto_id: produto.id,
                            produto_nome: produto.nome,
                            ...previsaoResponse.data,
                            hasPrevisao: !previsaoResponse.data.mensagem
                        };
                    } catch (error) {
                        return null;
                    }
                })
            );
            
            const produtosValidos = produtosComPrevisao.filter(p => p && p.hasPrevisao);
            
            setDados({
                produtos: produtosValidos,
                total: produtosValidos.length,
                urgentes: produtosValidos.filter(p => p.urgencia === 'alta').length,
                esta_semana: produtosValidos.filter(p => ['alta', 'média'].includes(p.urgencia)).length,
                valor_total_estimado: produtosValidos.reduce((total, p) => total + (p.valor_compra_sugerida || 0), 0)
            });
            
        } catch (error) {
            setError('Erro ao carregar dados pelo método alternativo.');
        }
    };

    const getPriceAnalysis = (produto) => {
        if (produto.analise_precos) {
            return produto.analise_precos;
        }
        
        if (produto.preco_custo_atual) {
            return {
                preco_atual: produto.preco_custo_atual,
                preco_ideal: produto.preco_custo_atual * 0.95,
                preco_maximo_aceitavel: produto.preco_custo_atual * 1.1,
                estrategia: 'estimativa',
                recomendacao: 'Preço estimado baseado no último custo'
            };
        }
        
        return null;
    };

    const getPriceIcon = (analisePrecos) => {
        if (!analisePrecos) return null;
        
        const diferenca = ((analisePrecos.preco_atual - analisePrecos.preco_ideal) / analisePrecos.preco_ideal) * 100;
        
        if (diferenca <= -5) return <FaArrowDown className="text-green-500" title="Preço abaixo do ideal" />;
        if (diferenca >= 10) return <FaArrowUp className="text-red-500" title="Preço acima do recomendado" />;
        return <FaMinus className="text-yellow-500" title="Preço dentro da faixa" />;
    };

    // ✅ FUNÇÃO DE FILTRO APRIMORADA
    const produtosFiltrados = dados?.produtos
        ?.filter(produto => {
            // Filtro por urgência
            if (filtro === 'urgentes' && produto.urgencia !== 'alta') return false;
            if (filtro === 'esta_semana' && !['alta', 'média'].includes(produto.urgencia)) return false;
            if (filtro === 'prioridade' && !priorityItems.has(produto.produto_id)) return false;
            
            // Filtro por busca
            if (searchTerm && !produto.produto_nome.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }
            
            // Filtro por preço
            const analisePrecos = getPriceAnalysis(produto);
            const valorTotal = analisePrecos ? analisePrecos.preco_ideal * produto.quantidade_sugerida_compra : 0;
            
            if (priceFilter === 'low' && valorTotal >= 100) return false;
            if (priceFilter === 'medium' && (valorTotal < 100 || valorTotal > 500)) return false;
            if (priceFilter === 'high' && valorTotal <= 500) return false;
            
            // Filtro por data
            if (dateRange === 'today' && produto.data_ideal_pedido !== 'AGORA') return false;
            if (dateRange === 'week' && !['alta', 'média'].includes(produto.urgencia)) return false;
            
            return true;
        })
        ?.sort((a, b) => {
            let comparison = 0;
            
            // Prioridades primeiro
            const aPriority = priorityItems.has(a.produto_id);
            const bPriority = priorityItems.has(b.produto_id);
            if (aPriority !== bPriority) {
                return aPriority ? -1 : 1;
            }
            
            switch (ordenacao) {
                case 'urgencia':
                    const urgenciaOrder = { 'alta': 3, 'média': 2, 'baixa': 1 };
                    comparison = (urgenciaOrder[b.urgencia] || 0) - (urgenciaOrder[a.urgencia] || 0);
                    break;
                case 'nome':
                    comparison = a.produto_nome.localeCompare(b.produto_nome);
                    break;
                case 'valor':
                    const valorA = getPriceAnalysis(a)?.preco_ideal * a.quantidade_sugerida_compra || 0;
                    const valorB = getPriceAnalysis(b)?.preco_ideal * b.quantidade_sugerida_compra || 0;
                    comparison = valorB - valorA;
                    break;
                case 'quantidade':
                    comparison = b.quantidade_sugerida_compra - a.quantidade_sugerida_compra;
                    break;
                case 'estoque':
                    comparison = a.estoque_atual - b.estoque_atual;
                    break;
                default:
                    comparison = 0;
            }
            
            return sortOrder === 'desc' ? comparison : -comparison;
        }) || [];

    const togglePriority = (produtoId) => {
        const newPriority = new Set(priorityItems);
        if (newPriority.has(produtoId)) {
            newPriority.delete(produtoId);
        } else {
            newPriority.add(produtoId);
        }
        setPriorityItems(newPriority);
    };

    const addNote = (produtoId, note) => {
        setNotes(prev => ({
            ...prev,
            [produtoId]: note
        }));
    };

    // ✅ FUNÇÕES DE EXPORTAÇÃO APRIMORADAS
    const exportarListaCompras = () => {
        const dataAtual = new Date().toLocaleDateString('pt-BR');
        const itensParaComprar = produtosFiltrados
            .map(produto => {
                const analisePrecos = getPriceAnalysis(produto);
                const precoIdeal = analisePrecos?.preco_ideal || 0;
                const isPriority = priorityItems.has(produto.produto_id);
                const note = notes[produto.produto_id];
                
                return {
                    nome: produto.produto_nome,
                    quantidade: produto.quantidade_sugerida_compra,
                    preco_max: precoIdeal,
                    urgencia: produto.urgencia,
                    prioridade: isPriority,
                    observacao: note || '',
                    valor_total: precoIdeal * produto.quantidade_sugerida_compra
                };
            });

        const resumo = [
            `🛒 LISTA DE COMPRAS INTELIGENTE - ${dataAtual}`,
            `📊 Total de itens: ${itensParaComprar.length}`,
            `💰 Valor estimado: R$ ${itensParaComprar.reduce((total, item) => total + item.valor_total, 0).toFixed(2)}`,
            `⚡ Itens prioritários: ${itensParaComprar.filter(i => i.prioridade).length}`,
            `🚨 Itens urgentes: ${itensParaComprar.filter(i => i.urgencia === 'alta').length}`,
            '',
            '--- LISTA DE COMPRAS ---',
            ...itensParaComprar.map(item => {
                const priority = item.prioridade ? '⭐ ' : '';
                const urgency = item.urgencia === 'alta' ? '🚨 ' : item.urgencia === 'média' ? '⚠️ ' : '';
                const note = item.observacao ? ` (${item.observacao})` : '';
                return `${priority}${urgency}${item.nome} - ${item.quantidade} unid - Até R$ ${item.preco_max.toFixed(2)}${note}`;
            })
        ].join('\n');

        const blob = new Blob([resumo], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `lista-compras-${new Date().toISOString().split('T')[0]}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const compartilharWhatsApp = () => {
        const itens = produtosFiltrados.slice(0, 10);
        const mensagem = `🛒 *Lista de Compras*\n\n${itens.map(produto => {
            const analisePrecos = getPriceAnalysis(produto);
            const precoIdeal = analisePrecos?.preco_ideal || 0;
            const priority = priorityItems.has(produto.produto_id) ? '⭐ ' : '';
            return `${priority}${produto.produto_nome} - ${produto.quantidade_sugerida_compra} unid`;
        }).join('\n')}\n\n💰 Valor estimado: R$ ${itens.reduce((total, produto) => {
            const analisePrecos = getPriceAnalysis(produto);
            return total + (analisePrecos?.preco_ideal || 0) * produto.quantidade_sugerida_compra;
        }, 0).toFixed(2)}`;
        
        const url = `https://wa.me/?text=${encodeURIComponent(mensagem)}`;
        window.open(url, '_blank');
    };

    const copiarLista = async () => {
        const itens = produtosFiltrados
            .map(produto => {
                const analisePrecos = getPriceAnalysis(produto);
                const precoIdeal = analisePrecos?.preco_ideal || 0;
                const priority = priorityItems.has(produto.produto_id) ? '⭐ ' : '';
                return `${priority}${produto.produto_nome} - ${produto.quantidade_sugerida_compra} unid - R$ ${precoIdeal.toFixed(2)}`;
            }).join('\n');
            
        try {
            await navigator.clipboard.writeText(itens);
            alert('Lista copiada para a área de transferência!');
        } catch (error) {
            console.error('Erro ao copiar:', error);
        }
    };

    // ✅ COMPONENTE DE CARD APRIMORADO
    const ProductCard = ({ produto }) => {
        const analisePrecos = getPriceAnalysis(produto);
        const isPriority = priorityItems.has(produto.produto_id);
        const productNote = notes[produto.produto_id];
        
        const urgencyColors = {
            alta: 'border-red-400 bg-red-50',
            média: 'border-yellow-400 bg-yellow-50',
            baixa: 'border-green-400 bg-green-50'
        };

        const urgencyIcons = {
            alta: <FaExclamationTriangle className="text-red-500" />,
            média: <FaClock className="text-yellow-500" />,
            baixa: <FaCheckCircle className="text-green-500" />
        };

        const valorTotal = analisePrecos ? analisePrecos.preco_ideal * produto.quantidade_sugerida_compra : 0;

        return (
            <div className={`p-4 rounded-lg border-2 transition-all relative ${
                isPriority ? 'border-orange-400 bg-orange-50 shadow-lg' :
                urgencyColors[produto.urgencia]
            } ${compactMode ? 'p-3' : 'p-4'}`}>
                
                {/* Badge de Prioridade */}
                {isPriority && (
                    <div className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                        PRIORIDADE
                    </div>
                )}

                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3 flex-1">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                                {urgencyIcons[produto.urgencia]}
                                <h3 className={`font-semibold ${compactMode ? 'text-base' : 'text-lg'} text-gray-800`}>
                                    {produto.produto_nome}
                                </h3>
                                {getPriceIcon(analisePrecos)}
                                
                                {/* Botão de prioridade */}
                                <div className="flex gap-1 ml-auto">
                                    <button
                                        onClick={() => togglePriority(produto.produto_id)}
                                        className={`p-1 rounded-full transition-colors ${
                                            isPriority ? 'text-orange-500 bg-orange-100' : 'text-gray-400 hover:text-orange-500'
                                        }`}
                                    >
                                        <FaFlag size={14} />
                                    </button>
                                </div>
                            </div>
                            
                            {!compactMode && (
                                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                                    <div>
                                        <p><FaBoxOpen className="inline mr-1" /><strong>Comprar:</strong> {produto.quantidade_sugerida_compra} unid</p>
                                        <p><FaCalendarAlt className="inline mr-1" /><strong>Quando:</strong> {produto.data_ideal_pedido}</p>
                                    </div>
                                    <div>
                                        <p><strong>Estoque atual:</strong> {produto.estoque_atual} unid</p>
                                        <p><strong>Cobertura:</strong> {produto.dias_cobertura_atual} dias</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    
                    <button
                        onClick={() => setProdutoDetalhado(produto)}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Ver detalhes"
                    >
                        <FaEye />
                    </button>
                </div>

                {/* Seção de Preços Simplificada */}
                {analisePrecos && !compactMode && (
                    <div className="mt-4 pt-3 border-t border-gray-200">
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Preço ideal:</span>
                            <span className="font-bold text-green-600">
                                R$ {analisePrecos.preco_ideal.toFixed(2)}
                            </span>
                        </div>
                        <div className="flex justify-between items-center mt-1">
                            <span className="text-sm text-gray-600">Total estimado:</span>
                            <span className="font-bold text-blue-600">
                                R$ {valorTotal.toFixed(2)}
                            </span>
                        </div>
                    </div>
                )}

                {/* Seção de Notas */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                    <input
                        type="text"
                        placeholder="Adicionar observação..."
                        value={productNote || ''}
                        onChange={(e) => addNote(produto.produto_id, e.target.value)}
                        className="w-full text-sm bg-transparent border-none outline-none placeholder-gray-400"
                    />
                </div>
            </div>
        );
    };

    // ✅ COMPONENTE DE VISTA COMPACTA
    const ProductListItem = ({ produto }) => {
        const analisePrecos = getPriceAnalysis(produto);
        const isPriority = priorityItems.has(produto.produto_id);
        const valorTotal = analisePrecos ? analisePrecos.preco_ideal * produto.quantidade_sugerida_compra : 0;

        return (
            <div className={`flex items-center gap-3 p-3 border-b border-gray-200 hover:bg-gray-50 ${
                isPriority ? 'bg-orange-50 border-orange-200' : ''
            }`}>
                
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        {isPriority && <FaFlag className="text-orange-500" size={12} />}
                        <span className="font-medium truncate">
                            {produto.produto_nome}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                            produto.urgencia === 'alta' ? 'bg-red-100 text-red-700' :
                            produto.urgencia === 'média' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                        }`}>
                            {produto.urgencia}
                        </span>
                    </div>
                    <div className="text-sm text-gray-500">
                        {produto.quantidade_sugerida_compra} unid • R$ {valorTotal.toFixed(2)}
                    </div>
                </div>
                
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => togglePriority(produto.produto_id)}
                        className={`p-1 ${isPriority ? 'text-orange-500' : 'text-gray-400'}`}
                    >
                        <FaFlag size={12} />
                    </button>
                    <button
                        onClick={() => setProdutoDetalhado(produto)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                    >
                        <FaEye size={12} />
                    </button>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <div className="bg-white p-8 rounded-2xl shadow-lg">
                    <div className="flex justify-center items-center py-12">
                        <FaSpinner className="animate-spin text-blue-500 text-4xl mr-4" />
                        <p className="text-gray-600 font-medium">Carregando lista de compras inteligente...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <div className="bg-white p-8 rounded-2xl shadow-lg">
                    <div className="text-center py-8">
                        <FaExclamationTriangle className="mx-auto text-red-500 mb-4" size={32} />
                        <p className="text-red-600 font-medium mb-4">{error}</p>
                        <button 
                            onClick={carregarDados}
                            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                        >
                            Tentar Novamente
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalItens = produtosFiltrados.length;
    const valorTotalEstimado = produtosFiltrados
        .reduce((total, produto) => {
            const analisePrecos = getPriceAnalysis(produto);
            return total + (analisePrecos?.preco_ideal || 0) * produto.quantidade_sugerida_compra;
        }, 0);

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <div className="bg-white p-6 rounded-2xl shadow-lg">
                {/* Header Aprimorado */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                    <div className="flex items-center mb-4 md:mb-0">
                        <FaShoppingCart className="text-3xl text-blue-500 mr-3" />
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Lista de Compras Inteligente</h1>
                            <p className="text-gray-600 text-sm">
                                {totalItens} itens • {dados?.urgentes || 0} urgentes • R$ {valorTotalEstimado.toFixed(2)} estimado
                            </p>
                        </div>
                    </div>
                    
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`p-3 rounded-lg transition-colors ${
                                showFilters ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title="Filtros avançados"
                        >
                            <FaFilter />
                        </button>
                        <button
                            onClick={carregarDados}
                            className="p-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                            title="Atualizar dados"
                        >
                            <FaSync />
                        </button>
                        <button
                            onClick={exportarListaCompras}
                            className="p-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                            title="Exportar lista"
                        >
                            <FaDownload />
                        </button>
                    </div>
                </div>

                {/* Barra de Busca */}
                <div className="mb-4">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Buscar produtos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes />
                            </button>
                        )}
                    </div>
                </div>

                {/* Filtros Avançados */}
                {showFilters && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {/* Filtro por Urgência */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Urgência</label>
                                <select
                                    value={filtro}
                                    onChange={(e) => setFiltro(e.target.value)}
                                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="todos">Todos</option>
                                    <option value="urgentes">🚨 Urgentes</option>
                                    <option value="esta_semana">📅 Esta Semana</option>
                                    <option value="prioridade">⭐ Prioridade</option>
                                </select>
                            </div>

                            {/* Filtro por Preço */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Faixa de Preço</label>
                                <select
                                    value={priceFilter}
                                    onChange={(e) => setPriceFilter(e.target.value)}
                                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="all">Todos os valores</option>
                                    <option value="low">Até R$ 100</option>
                                    <option value="medium">R$ 100 - R$ 500</option>
                                    <option value="high">Acima de R$ 500</option>
                                </select>
                            </div>

                            {/* Ordenação */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Ordenar por</label>
                                <div className="flex gap-2">
                                    <select
                                        value={ordenacao}
                                        onChange={(e) => setOrdenacao(e.target.value)}
                                        className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="urgencia">🔥 Urgência</option>
                                        <option value="nome">🔤 Nome</option>
                                        <option value="valor">💰 Valor</option>
                                        <option value="quantidade">📦 Quantidade</option>
                                        <option value="estoque">📊 Estoque</option>
                                    </select>
                                    <button
                                        onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
                                        className="p-2 border rounded-lg hover:bg-gray-100"
                                        title={`Ordenação ${sortOrder === 'desc' ? 'decrescente' : 'crescente'}`}
                                    >
                                        {sortOrder === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                                    </button>
                                </div>
                            </div>

                            {/* Modo de Visualização */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Visualização</label>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setViewMode('cards')}
                                        className={`flex-1 p-2 rounded-lg transition-colors ${
                                            viewMode === 'cards' ? 'bg-blue-500 text-white' : 'bg-white border hover:bg-gray-50'
                                        }`}
                                    >
                                        Cards
                                    </button>
                                    <button
                                        onClick={() => setViewMode('list')}
                                        className={`flex-1 p-2 rounded-lg transition-colors ${
                                            viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white border hover:bg-gray-50'
                                        }`}
                                    >
                                        Lista
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Opções Adicionais */}
                        <div className="mt-4 flex flex-wrap gap-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={compactMode}
                                    onChange={(e) => setCompactMode(e.target.checked)}
                                    className="mr-2"
                                />
                                Modo compacto
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={showStats}
                                    onChange={(e) => setShowStats(e.target.checked)}
                                    className="mr-2"
                                />
                                Mostrar estatísticas
                            </label>
                        </div>
                    </div>
                )}

                {/* Estatísticas Inteligentes */}
                {showStats && (
                    <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-gradient-to-r from-red-50 to-red-100 p-4 rounded-lg border border-red-200">
                            <div className="flex items-center gap-2 mb-2">
                                <FaExclamationTriangle className="text-red-500" />
                                <span className="font-medium text-red-700">Urgentes</span>
                            </div>
                            <div className="text-2xl font-bold text-red-600">
                                {produtosFiltrados.filter(p => p.urgencia === 'alta').length}
                            </div>
                            <div className="text-sm text-red-600">
                                Precisam de atenção imediata
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg border border-green-200">
                            <div className="flex items-center gap-2 mb-2">
                                <FaDollarSign className="text-green-500" />
                                <span className="font-medium text-green-700">Valor Total</span>
                            </div>
                            <div className="text-2xl font-bold text-green-600">
                                R$ {valorTotalEstimado.toFixed(0)}
                            </div>
                            <div className="text-sm text-green-600">
                                Investimento estimado
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg border border-blue-200">
                            <div className="flex items-center gap-2 mb-2">
                                <FaBoxOpen className="text-blue-500" />
                                <span className="font-medium text-blue-700">Total de Itens</span>
                            </div>
                            <div className="text-2xl font-bold text-blue-600">
                                {totalItens}
                            </div>
                            <div className="text-sm text-blue-600">
                                produtos para comprar
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg border border-purple-200">
                            <div className="flex items-center gap-2 mb-2">
                                <FaFlag className="text-purple-500" />
                                <span className="font-medium text-purple-700">Prioridades</span>
                            </div>
                            <div className="text-2xl font-bold text-purple-600">
                                {produtosFiltrados.filter(p => priorityItems.has(p.produto_id)).length}
                            </div>
                            <div className="text-sm text-purple-600">
                                Itens marcados
                            </div>
                        </div>
                    </div>
                )}

                {/* Ações Rápidas */}
                <div className="mb-6 flex flex-wrap gap-2">
                    <button
                        onClick={() => {
                            const urgentes = produtosFiltrados.filter(p => p.urgencia === 'alta').map(p => p.produto_id);
                            setPriorityItems(new Set(urgentes));
                        }}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center gap-2"
                    >
                        <FaFlag /> Priorizar Urgentes
                    </button>
                    <button
                        onClick={exportarListaCompras}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center gap-2"
                    >
                        <FaDownload /> Exportar Lista
                    </button>
                    <button
                        onClick={compartilharWhatsApp}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                    >
                        <FaWhatsapp /> WhatsApp
                    </button>
                </div>

                {/* Lista de Produtos */}
                {produtosFiltrados.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <FaCheckCircle className="mx-auto text-4xl text-green-500 mb-4" />
                        <p className="text-gray-600 text-lg">✅ Nenhum item encontrado!</p>
                        <p className="text-gray-400 text-sm mt-2">
                            {searchTerm 
                                ? `Nenhum produto encontrado para "${searchTerm}"`
                                : filtro === 'urgentes' 
                                    ? 'Nenhum item urgente encontrado.' 
                                    : 'Ajuste os filtros para ver mais itens.'
                            }
                        </p>
                    </div>
                ) : viewMode === 'list' ? (
                    <div className="bg-white rounded-lg border">
                        <div className="p-4 border-b bg-gray-50 rounded-t-lg">
                            <h3 className="font-semibold text-gray-700">Lista de Compras ({produtosFiltrados.length} itens)</h3>
                        </div>
                        <div className="max-h-96 overflow-y-auto">
                            {produtosFiltrados.map((produto) => (
                                <ProductListItem key={produto.produto_id} produto={produto} />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {produtosFiltrados.map((produto) => (
                            <ProductCard key={produto.produto_id} produto={produto} />
                        ))}
                    </div>
                )}

                {/* Resumo Final Aprimorado */}
                {produtosFiltrados.length > 0 && (
                    <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 via-green-50 to-purple-50 rounded-lg border border-blue-200">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                            <FaChartLine /> Resumo Inteligente da Lista
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                            <div className="text-center">
                                <p className="text-2xl font-bold text-blue-600">{totalItens}</p>
                                <p className="text-sm text-gray-600">Itens Total</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-orange-600">
                                    {produtosFiltrados.filter(p => priorityItems.has(p.produto_id)).length}
                                </p>
                                <p className="text-sm text-gray-600">Prioritários</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-red-600">
                                    {produtosFiltrados.filter(p => p.urgencia === 'alta').length}
                                </p>
                                <p className="text-sm text-gray-600">Urgentes</p>
                            </div>
                            <div className="text-center">
                                <p className="text-2xl font-bold text-purple-600">R$ {valorTotalEstimado.toFixed(0)}</p>
                                <p className="text-sm text-gray-600">Valor Estimado</p>
                            </div>
                        </div>
                        
                        {/* Dicas Inteligentes */}
                        <div className="bg-white rounded-lg p-4 border border-blue-200 mb-4">
                            <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                                <FaLightbulb className="text-yellow-500" /> Dicas Inteligentes
                            </h4>
                            <div className="text-sm text-gray-600 space-y-1">
                                {produtosFiltrados.filter(p => p.urgencia === 'alta').length > 0 && (
                                    <p>🚨 Você tem {produtosFiltrados.filter(p => p.urgencia === 'alta').length} item(ns) urgente(s) - compre primeiro!</p>
                                )}
                                {valorTotalEstimado > 1000 && (
                                    <p>💡 Lista com valor alto - considere dividir as compras em etapas.</p>
                                )}
                                {produtosFiltrados.filter(p => priorityItems.has(p.produto_id)).length > 0 && (
                                    <p>⭐ Você tem {produtosFiltrados.filter(p => priorityItems.has(p.produto_id)).length} item(ns) prioritário(s) na lista.</p>
                                )}
                                <p>🚩 Use as bandeiras para marcar itens prioritários e organize melhor suas compras.</p>
                            </div>
                        </div>
                        
                        <div className="flex flex-col md:flex-row gap-3 justify-center">
                            <button
                                onClick={() => {
                                    const urgentes = produtosFiltrados.filter(p => p.urgencia === 'alta').map(p => p.produto_id);
                                    setPriorityItems(new Set(urgentes));
                                }}
                                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <FaFlag /> Priorizar Urgentes
                            </button>
                            <button
                                onClick={compartilharWhatsApp}
                                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <FaWhatsapp /> WhatsApp
                            </button>
                            <button
                                onClick={exportarListaCompras}
                                className="px-6 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
                            >
                                <FaDownload /> Exportar Completa
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de Detalhes Aprimorado */}
            <ModalDetalhes 
                produto={produtoDetalhado} 
                onClose={() => setProdutoDetalhado(null)}
                onTogglePriority={togglePriority}
                isPriority={produtoDetalhado && priorityItems.has(produtoDetalhado.produto_id)}
                note={produtoDetalhado && notes[produtoDetalhado.produto_id]}
                onAddNote={addNote}
            />
        </div>
    );
};

// Modal de Detalhes Aprimorado
const ModalDetalhes = ({ produto, onClose, onTogglePriority, isPriority, note, onAddNote }) => {
    if (!produto) return null;
    
    const analisePrecos = produto.analise_precos || {
        preco_atual: produto.preco_custo_atual || 0,
        preco_ideal: (produto.preco_custo_atual || 0) * 0.95,
        preco_maximo_aceitavel: (produto.preco_custo_atual || 0) * 1.1,
        estrategia: 'estimativa'
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-96 overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <h3 className="text-xl font-semibold text-gray-800">{produto.produto_nome}</h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => onTogglePriority(produto.produto_id)}
                                    className={`p-2 rounded-full transition-colors ${
                                        isPriority ? 'text-orange-500 bg-orange-100' : 'text-gray-400 hover:text-orange-500'
                                    }`}
                                    title="Marcar como prioridade"
                                >
                                    <FaFlag />
                                </button>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                            <FaTimes size={20} />
                        </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Informações de Estoque */}
                        <div className="space-y-4">
                            <h4 className="font-semibold text-gray-700 border-b pb-2">📦 Informações de Estoque</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Estoque atual:</span>
                                    <span className="font-semibold">{produto.estoque_atual} unidades</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Consumo médio:</span>
                                    <span className="font-semibold">{produto.consumo_medio_diario} unid/dia</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Lead time:</span>
                                    <span className="font-semibold">{produto.lead_time_dias} dias</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Rotatividade ideal:</span>
                                    <span className="font-semibold">{produto.rotatividade_target} dias</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Análise de Preços Detalhada */}
                        <div className="space-y-4">
                            <h4 className="font-semibold text-gray-700 border-b pb-2">💰 Análise de Preços</h4>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Preço atual:</span>
                                    <span className="font-semibold">R$ {analisePrecos.preco_atual.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Preço ideal:</span>
                                    <span className="font-semibold text-green-600">R$ {analisePrecos.preco_ideal.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">Máximo aceitável:</span>
                                    <span className="font-semibold text-red-600">R$ {analisePrecos.preco_maximo_aceitavel.toFixed(2)}</span>
                                </div>
                                {analisePrecos.volatilidade && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Volatilidade:</span>
                                        <span className="font-semibold">{analisePrecos.volatilidade}%</span>
                                    </div>
                                )}
                                {analisePrecos.amostras && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Baseado em:</span>
                                        <span className="font-semibold">{analisePrecos.amostras} compras</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Seção de Notas */}
                    <div className="mt-6">
                        <h4 className="font-semibold text-gray-700 mb-2">📝 Observações</h4>
                        <textarea
                            placeholder="Adicione suas observações sobre este item..."
                            value={note || ''}
                            onChange={(e) => onAddNote(produto.produto_id, e.target.value)}
                            className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500"
                            rows="3"
                        />
                    </div>
                    
                    {/* Recomendações */}
                    {produto.recomendacoes && produto.recomendacoes.length > 0 && (
                        <div className="mt-6 bg-blue-50 p-4 rounded-lg">
                            <h4 className="font-semibold text-blue-800 mb-2">💡 Recomendações</h4>
                            <ul className="space-y-1 text-blue-700 text-sm">
                                {produto.recomendacoes.map((rec, idx) => (
                                    <li key={idx}>• {rec}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShoppingDashboard;